import { useEffect, useRef } from 'react';

import { rebindThisLocationDevice } from '@/lib/push/locationSubscription';

import { useLocationSession } from './useLocationSession';

/**
 * Rebinds this device's push subscription to the currently selected group
 * whenever a location viewer's group changes (group switch in the modal,
 * or restore-from-storage on load). No-op for every other viewer kind.
 * Mounted once, app-wide, inside LocationSessionProvider (App.tsx's
 * GuardedLayout) — ParentMessages alone isn't enough because group changes
 * happen in LocationGate, not on the messages page.
 *
 * Rebinds are serialized through a ref-held promise chain so that a rapid
 * group switch (B -> C) can't race: even if B's RPC would otherwise resolve
 * after C's, B's call is queued behind C's and only starts once C's finishes,
 * so the endpoint always ends up bound to the latest group.
 */
export function useLocationPushRebind(): void {
  const { isLocationViewer, session } = useLocationSession();
  const groupId = session?.group.id;
  const chainRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (!isLocationViewer || !groupId) return;
    chainRef.current = chainRef.current.then(() => rebindThisLocationDevice(groupId)).catch((err: unknown) => {
      console.error('[push] rebind failed', err);
    });
  }, [isLocationViewer, groupId]);
}
